/**
 * Profit Prediction Focus - Field Data Configuration
 * Version 3 - Prediction Focus = Profit
 */

import { FieldData } from "./FTDConfig";

/**
 * Lookup object for Profit prediction focus fields
 * Key = Field name exactly as it appears in your table
 */
export const PROFIT_FIELDS: Record<string, FieldData> = {
  Growth: {
    type: "Formula",
    formula: "[Active Users / Last Full Month Active Users] - 1",
    affectedFields: "",
    formulaFields: "Active Users",
  },

  "Active Users": {
    type: "Formula",
    formula: "Old Users + Acquired Users",
    affectedFields: "Growth, Net Revenue",
    formulaFields: "Old Users, Acquired Users",
  },

  "Old Users": {
    type: "Formula",
    formula: "(Last Full Month Active Users) * (1 - Churn Rate)",
    affectedFields: "Active Users",
    formulaFields: "Churn Rate, Active Users",
  },

  "Acquired Users": {
    type: "Formula",
    formula: "FTD * (Acquired Users / FTD Ratio)",
    affectedFields: "Active Users",
    formulaFields: "FTD, Acquired Users / FTD Ratio",
  },

  "Churn Rate": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Old Users",
  },

  FTD: {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Acquired Users, RS PV, CPA Income, Clicks, Spend",
  },

  "Acquired Users / FTD Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Acquired Users",
  },

  "Average Net Revenue Per Active User": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Net Revenue",
  },

  "Estimated Net Revenue": {
    type: "Formula",
    formula:
      "Average Net Revenue Per Active User * Active Users * [RS BR to NG Active Ratio]",
    affectedFields: "RS",
    formulaFields:
      "Average Net Revenue Per Active User, Active Users, RS BR to NG Active Ratio",
  },

  "Average RS %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "RS",
  },

  "RS BR to NG Active Ratio": {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Net Revenue",
  },

  "Estimated Revenue Share": {
    type: "Formula",
    formula: "Average Net Revenue * Average RS percentage",
    affectedFields: "RS PV, Total Income, EPC RS",
    formulaFields: "Estimated Net Revenue, Average RS %",
  },

  "RS PV": {
    type: "Formula",
    formula: "Estimated RS / FTD",
    affectedFields: "",
    formulaFields: "Estimated Revenue Share, FTD",
  },

  "CPA PV": {
    type: "User input",
    glossaryText: "By Default, Last full month value",
    affectedFields: "CPA Income",
  },

  "Estimated CPA Income": {
    type: "Formula",
    formula: "CPA PV * FTD",
    affectedFields: "Total Income, EPC CPA",
    formulaFields: "CPA PV, FTD",
  },

  "Total Income": {
    type: "Formula",
    formula: "Estimated RevShare + Estimated CPA Income",
    affectedFields: "Profit",
    formulaFields: "Estimated Revenue Share, Estimated CPA Income",
  },

  "Conversion %": {
    type: "User input",
    glossaryText: "By Default, Average of the last 6 full months",
    affectedFields: "Clicks",
  },

  Clicks: {
    type: "Formula",
    formula: "FTD / Conversion Percentage",
    affectedFields: "EPC CPA, EPC RS, CPC",
    formulaFields: "FTD, Conversion %",
  },

  "EPC CPA": {
    type: "Formula",
    formula: "Estimated CPA Income / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated CPA Income, Clicks",
  },

  "EPC RS": {
    type: "Formula",
    formula: "Estimated Revenue Share / Clicks",
    affectedFields: "EPC",
    formulaFields: "Estimated Revenue Share, Clicks",
  },

  EPC: {
    type: "Formula",
    formula: "EPC CPA + EPC RS",
    affectedFields: "",
    formulaFields: "EPC CPA, EPC RS",
  },

  CPC: {
    type: "Formula",
    formula: "Spend / Clicks",
    affectedFields: "",
    formulaFields: "Spend, Clicks",
  },

  CAC: {
    type: "User input",
    glossaryText: "By Default, Average of the last 3 full months",
    affectedFields: "Spend",
  },

  Spend: {
    type: "Formula",
    formula: "FTD * CAC",
    affectedFields: "Profit, CPC",
    formulaFields: "FTD, CAC",
  },

  Profit: {
    type: "Formula",
    formula: "Total Income - Spend",
    affectedFields: "",
    formulaFields: "Total Income, Spend",
  },
};

/**
 * Field Names Constants
 */
export const FIELD_NAMES = {
  GROWTH: "Growth",
  ACTIVE_USERS: "Active Users",
  OLD_USERS: "Old Users",
  ACQUIRED_USERS: "Acquired Users",
  CHURN_RATE: "Churn Rate",
  FTD: "FTD",
  ACQUIRED_TO_FTD: "Acquired Users / FTD Ratio",
  AVG_NET_REV: "Average Net Revenue Per Active User",
  NET_REVENUE: "Estimated Net Revenue",
  AVG_RS_P: "Average RS %",
  RS_BR_NG: "RS BR to NG Active Ratio",
  ESTIMATED_REVENUE_SHARE: "Estimated Revenue Share",
  RS_PV: "RS PV",
  CPA_PV: "CPA PV",
  CPA_INCOME: "Estimated CPA Income",
  TOTAL_INCOME: "Total Income",
  CVR: "Conversion %",
  CLICKS: "Clicks",
  EPC_CPA: "EPC CPA",
  EPC_RS: "EPC RS",
  EPC: "EPC",
  CPC: "CPC",
  CAC: "CAC",
  SPEND: "Spend",
  PROFIT: "Profit",
  NRPU: "NRPU",
} as const;

/**
 * Simple helper to get Profit field info
 */
export const getProfitFieldInfo = (fieldName: string) => {
  return PROFIT_FIELDS[fieldName] || null;
};
